<template>
  <div v-if="visible" class="mask mask--scroll" @click.self="$emit('close')">
    <div class="dialog dialog--shell dialog--lg engagement-detail-dlg" @click.stop>
      <div class="dialog-head">
        <h3 class="dialog-title">活跃度详情</h3>
        <button type="button" class="dialog-close" aria-label="关闭" @click="$emit('close')">×</button>
      </div>
      <div class="dialog-body">
        <p v-if="loading" class="muted">加载中…</p>
        <p v-else-if="errorMsg" class="error">{{ errorMsg }}</p>
        <template v-else-if="detail">
          <div class="member-profile-head">
            <div class="member-profile-head__item">
              <span class="member-profile-head__label">姓名</span>
              <span class="member-profile-head__value">{{ detail.registerName || '—' }}</span>
            </div>
            <div class="member-profile-head__item">
              <span class="member-profile-head__label">手机</span>
              <span class="member-profile-head__value member-profile-head__value--mono">{{ detail.registerMobile || '—' }}</span>
            </div>
            <div class="member-profile-head__item member-profile-head__item--full">
              <span class="member-profile-head__label">单位</span>
              <span class="member-profile-head__value" :title="detail.registerCompanyName || ''">{{ detail.registerCompanyName || '—' }}</span>
            </div>
          </div>

          <div class="metric-grid metric-grid--views">
            <div class="metric-card"><span class="k">报名</span><span class="v">{{ detail.signupCount }}</span></div>
            <div class="metric-card"><span class="k">签到</span><span class="v">{{ detail.checkinCount }}</span></div>
            <div class="metric-card metric-card--wide">
              <span class="k">浏览</span>
              <div class="view-breakdown">
                <div v-for="line in viewBreakdownLines" :key="line">{{ line }}</div>
              </div>
            </div>
            <div class="metric-card"><span class="k">问卷</span><span class="v">{{ detail.surveyCount }}</span></div>
          </div>
          <p class="metric-sum muted">行为合计 {{ detailTotal }} = 报名 + 签到 + 浏览 + 问卷（明细 {{ detail.events?.length || 0 }} 条）</p>

          <div class="mini-chart-grid">
            <div class="mini-chart-card">
              <div class="mini-chart-title">个人趋势</div>
              <div ref="trendRef" class="mini-chart-box"></div>
            </div>
            <div class="mini-chart-card">
              <div class="mini-chart-title">行为构成</div>
              <div ref="compositionRef" class="mini-chart-box"></div>
            </div>
          </div>

          <div class="detail-filter">
            <FancySelect v-model="typeFilter" class="flt-select" :options="typeOptions" placeholder="明细类型" @change="load" />
          </div>

          <ul v-if="detail.events?.length" class="event-list">
            <li v-for="(ev, idx) in detail.events" :key="idx" class="event-item">
              <span class="event-time">{{ ev.occurredAt }}</span>
              <span class="pill pill-blue">{{ ev.typeLabel }}</span>
              <span class="event-title">{{ ev.title || '—' }}</span>
            </li>
          </ul>
          <p v-else class="muted table-empty">暂无明细</p>
        </template>
      </div>
      <div class="dialog-foot">
        <button type="button" class="btn btn-cancel" @click="$emit('close')">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import FancySelect from '@/components/FancySelect.vue'
import { getAdminMemberEngagementDetails } from '@/api'
import { ENGAGEMENT_CHART_COLORS, engagementGrid, engagementViewBreakdownLines, engagementViewTotal } from '@/utils/engagementCharts.js'

export default {
  name: 'MemberEngagementDetailDialog',
  components: { FancySelect },
  props: {
    visible: { type: Boolean, default: false },
    memberId: { type: [Number, String], default: null },
    guestPhone: { type: String, default: '' },
    rangeParams: { type: Object, default: () => ({}) },
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      errorMsg: '',
      detail: null,
      typeFilter: '',
      trendChart: null,
      compositionChart: null,
      typeOptions: [
        { value: '', label: '全部明细' },
        { value: 'signup', label: '活动报名' },
        { value: 'checkin', label: '活动签到' },
        { value: 'view', label: '全部浏览' },
        { value: 'article_view', label: '资讯浏览' },
        { value: 'album_view', label: '相册浏览' },
        { value: 'activity_view', label: '活动浏览' },
        { value: 'supply_view', label: '供需浏览' },
        { value: 'survey', label: '问卷提交' },
      ],
    }
  },
  computed: {
    viewBreakdownLines() {
      return this.detail ? engagementViewBreakdownLines(this.detail) : []
    },
    detailTotal() {
      const d = this.detail
      if (!d) return 0
      return (d.signupCount || 0) + (d.checkinCount || 0) + engagementViewTotal(d) + (d.surveyCount || 0)
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.typeFilter = ''
        this.disposeCharts()
        this.load()
      } else {
        this.disposeCharts()
      }
    },
    memberId() {
      if (this.visible) this.load()
    },
    guestPhone() {
      if (this.visible) this.load()
    },
  },
  beforeUnmount() {
    this.disposeCharts()
    window.removeEventListener('resize', this.handleResize)
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    async load() {
      const phone = String(this.guestPhone || '').trim()
      const memberId = Number(this.memberId)
      if (!phone && !(memberId > 0)) return
      this.loading = true
      this.errorMsg = ''
      try {
        const params = { ...(this.rangeParams || {}) }
        if (this.typeFilter) params.type = this.typeFilter
        if (phone) {
          params.phone = phone
          this.detail = await getAdminMemberEngagementDetails(0, params)
        } else {
          this.detail = await getAdminMemberEngagementDetails(memberId, params)
        }
        this.$nextTick(() => {
          this.initCharts()
          this.renderCharts()
        })
      } catch (e) {
        this.errorMsg = e.message || '加载详情失败'
        this.detail = null
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      if (this.$refs.trendRef && !this.trendChart) {
        this.trendChart = echarts.init(this.$refs.trendRef)
      }
      if (this.$refs.compositionRef && !this.compositionChart) {
        this.compositionChart = echarts.init(this.$refs.compositionRef)
      }
    },
    disposeCharts() {
      if (this.trendChart) {
        this.trendChart.dispose()
        this.trendChart = null
      }
      if (this.compositionChart) {
        this.compositionChart.dispose()
        this.compositionChart = null
      }
    },
    handleResize() {
      if (this.trendChart) this.trendChart.resize()
      if (this.compositionChart) this.compositionChart.resize()
    },
    renderCharts() {
      const d = this.detail
      if (!d) return
      const trend = d.dailyTrend || []
      if (this.trendChart) {
        this.trendChart.setOption({
          color: ['#2f6af6'],
          tooltip: { trigger: 'axis' },
          grid: engagementGrid(),
          xAxis: { type: 'category', data: trend.map((p) => p.label) },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f7' } } },
          series: [{ type: 'line', smooth: true, areaStyle: { opacity: 0.08 }, data: trend.map((p) => p.behaviorCount) }],
        })
      }
      const comp = d.composition || []
      if (this.compositionChart) {
        this.compositionChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie',
            radius: ['40%', '65%'],
            center: ['50%', '50%'],
            data: comp.map((s) => ({ name: s.name, value: s.value })),
            label: { formatter: '{b}\n{c}' },
          }],
        })
      }
    },
  },
}
</script>

<style scoped>
.engagement-detail-dlg { max-width: 920px; }
.member-profile-head {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  margin-bottom: 14px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f2f4f7 100%);
  border: 1px solid #eaecf0;
  border-radius: 12px;
}
.member-profile-head__item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.member-profile-head__item--full {
  grid-column: 1 / -1;
}
.member-profile-head__label {
  font-size: 12px;
  color: #667085;
  line-height: 1.2;
}
.member-profile-head__value {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  line-height: 1.4;
  word-break: break-word;
}
.member-profile-head__value--mono {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
}
.metric-card {
  border: 1px solid #e6edf8;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f8fbff;
}
.metric-card .k { display: block; font-size: 12px; color: #667085; }
.metric-card .v { display: block; font-size: 22px; font-weight: 800; color: #0b2a5b; margin-top: 4px; }
.metric-sum { margin: 0 0 10px; font-size: 12px; }
.mini-chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}
.mini-chart-card {
  border: 1px solid #e9eef5;
  border-radius: 12px;
  padding: 10px;
}
.mini-chart-title { font-size: 13px; font-weight: 700; margin-bottom: 6px; color: #344054; }
.mini-chart-box { height: 220px; width: 100%; }
.detail-filter { margin-bottom: 10px; max-width: 200px; }
.event-list { list-style: none; margin: 0; padding: 0; max-height: 280px; overflow: auto; }
.event-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
}
.event-time { color: #667085; min-width: 140px; }
.event-title { flex: 1; color: #1d2939; }
@media (max-width: 768px) {
  .member-profile-head {
    grid-template-columns: 1fr;
  }
  .metric-grid, .mini-chart-grid { grid-template-columns: 1fr; }
}
</style>
